import { Currency } from "@inrev/common";
import { cn } from "../../lib/utils";
import { formatMetricCardCurrency } from "../../patterns/metric-card";
import { Text } from "../../primitives";
import { PortaledTooltip } from "../../ui/Tooltip";
import {
	EXPOSURE_EMPHASIS_OVERLAY,
	EXPOSURE_SEGMENT_COLOR_CLASS,
	type ExposureSegment,
	type ExposureSegmentColumn,
} from "./types";

const EMPTY_COLUMNS: ExposureSegmentColumn[] = [];

const display = (amount: number) => formatMetricCardCurrency(amount).display;

const overlayFor = (segment: ExposureSegment) =>
	EXPOSURE_EMPHASIS_OVERLAY[segment.emphasis ?? "solid"];

const segmentAriaLabel = (segment: ExposureSegment) => {
	const total = `${segment.label}: ${display(segment.amount)}`;
	if (segment.breakdown === undefined) return total;
	const parts = segment.breakdown.map((part) => `${part.label} ${display(part.amount)}`).join(", ");
	return `${total} (${parts})`;
};

const Swatch = ({ segment }: { segment: ExposureSegment }) => {
	const overlay = overlayFor(segment);
	return (
		<div
			className={cn(
				"relative size-3 shrink-0 rounded-[3px]",
				EXPOSURE_SEGMENT_COLOR_CLASS[segment.color],
			)}
		>
			{overlay !== undefined && <div className="absolute inset-0 rounded-[3px]" style={overlay} />}
		</div>
	);
};

const totalsColumn = (columns: ExposureSegmentColumn[]): ExposureSegmentColumn => {
	const labels: string[] = [];
	const sums = new Map<string, number>();
	for (const column of columns) {
		for (const segment of column.segments) {
			const label = segment.legendLabel ?? segment.label;
			if (!sums.has(label)) labels.push(label);
			sums.set(label, Currency.add(sums.get(label) ?? 0, segment.amount));
		}
	}
	return {
		key: "total",
		label: "Total",
		plain: true,
		totalLabel: "",
		segments: labels.map((label) => ({
			id: `total-${label.toLowerCase()}`,
			label,
			legendLabel: label,
			amount: sums.get(label) ?? 0,
			color: "headroom",
		})),
	};
};

export type ExposureStackedBarProps = {
	columns?: ExposureSegmentColumn[];
	limit?: number;
	overLimit?: number;
	/** Unspent limit, drawn last to fill the track. */
	headroom?: ExposureSegment;
	/** List headroom as a Capacity / Available column. The bar still draws it as the track fill. */
	showHeadroomInLegend?: boolean;
	/** Keep $0 slices in the legend. The bar still skips them. */
	showZeroLegendRows?: boolean;
	/** Append a Total column that sums each legend row across the other columns. */
	showTotalsColumn?: boolean;
	testId?: string;
};

export const ExposureStackedBar = ({
	columns = EMPTY_COLUMNS,
	limit,
	overLimit,
	headroom,
	showHeadroomInLegend = false,
	showZeroLegendRows = false,
	showTotalsColumn = false,
	testId = "exposure-bar",
}: ExposureStackedBarProps) => {
	const capacityColumn: ExposureSegmentColumn | undefined =
		showHeadroomInLegend && headroom !== undefined && headroom.amount > 0
			? {
					key: "capacity",
					label: "Capacity",
					segments: [headroom],
				}
			: undefined;
	const sourceColumns = columns
		.map((column) => ({
			...column,
			segments: showZeroLegendRows
				? column.segments
				: column.segments.filter((segment) => segment.amount > 0),
		}))
		.filter((column) => showZeroLegendRows || column.segments.length > 0);
	const legendColumns = [
		...sourceColumns,
		...(capacityColumn === undefined ? [] : [capacityColumn]),
		...(showTotalsColumn ? [totalsColumn(sourceColumns)] : []),
	];
	const exposed = columns
		.flatMap((column) => column.segments)
		.filter((segment) => segment.amount > 0);
	const visible = headroom === undefined || headroom.amount <= 0 ? exposed : [...exposed, headroom];
	const used = Currency.sum(exposed.map((segment) => segment.amount));
	const isOverLimit = overLimit !== undefined;
	const trackTotal = limit !== undefined && limit > 0 && !isOverLimit ? limit : used;
	const tooltipId = `${testId}-segment-tooltip`;

	return (
		<div className="flex w-full flex-col" data-testid={testId}>
			<div
				className={cn(
					"flex h-6 w-full overflow-hidden rounded-lg bg-surface-neutral",
					isOverLimit && "border-2 border-danger",
				)}
				data-testid={`${testId}-track`}
			>
				{visible.map((segment, index) => {
					const overlay = overlayFor(segment);
					return (
						<div
							key={segment.id}
							role="img"
							tabIndex={0}
							aria-label={segmentAriaLabel(segment)}
							// Border-box keeps the separator inside the percentage width, so the segments
							// still sum to the full track.
							className={cn(
								"relative h-full shrink-0 cursor-help transition-[filter] hover:brightness-90 focus-visible:brightness-90",
								index > 0 && "border-bc-inverse border-l-2",
								EXPOSURE_SEGMENT_COLOR_CLASS[segment.color],
							)}
							style={{ width: `${trackTotal === 0 ? 0 : (segment.amount / trackTotal) * 100}%` }}
							data-tooltip-id={tooltipId}
							data-segment-id={segment.id}
							data-testid={`${testId}-segment-${segment.id}`}
						>
							{overlay !== undefined && (
								<div className="pointer-events-none absolute inset-0" style={overlay} />
							)}
						</div>
					);
				})}
			</div>
			<PortaledTooltip
				id={tooltipId}
				render={({ activeAnchor }) => {
					const segment = visible.find(
						(candidate) => candidate.id === activeAnchor?.getAttribute("data-segment-id"),
					);
					if (segment === undefined) return null;
					return (
						// `Text` omits `color` throughout so it inherits the tooltip's light-on-dark
						// treatment; the semantic tones are all tuned for light surfaces.
						<div className="flex flex-col gap-2.5">
							<Text type="body">{`${segment.label}: ${display(segment.amount)}`}</Text>
							{segment.breakdown !== undefined && (
								<div className="flex flex-col gap-2 border-white/25 border-t pt-1.5">
									{segment.breakdown.map((part) => (
										<div key={part.label} className="flex items-center justify-between gap-4">
											<Text type="caption" className="opacity-80">
												{part.label}
											</Text>
											<Text type="caption">{display(part.amount)}</Text>
										</div>
									))}
								</div>
							)}
						</div>
					);
				}}
			/>
			<div
				className="grid gap-x-6 pt-3"
				style={{ gridTemplateColumns: `repeat(${legendColumns.length}, minmax(0, 1fr))` }}
			>
				{legendColumns.map((column) => (
					<div
						key={column.key}
						className="flex min-w-0 flex-col gap-1"
						data-testid={`${testId}-legend-column-${column.key}`}
					>
						<Text type="eyebrow" color="tertiary" truncate>
							{column.label}
						</Text>
						{column.segments.map((segment) => (
							<div
								key={segment.id}
								className="flex min-w-0 items-center gap-1.5"
								data-testid={`${testId}-legend-${segment.id}`}
							>
								{!column.plain && <Swatch segment={segment} />}
								<Text type="caption" color="secondary" truncate className="min-w-0">
									{segment.legendLabel ?? segment.label}
								</Text>
								<Text type="caption" color="tertiary" className="ml-auto shrink-0">
									{display(segment.amount)}
								</Text>
							</div>
						))}
						{column.totalLabel !== undefined && (
							<div
								className="mt-0.5 flex min-w-0 items-center gap-1.5 border-bc-default border-t pt-1.5"
								data-testid={`${testId}-legend-total-${column.key}`}
							>
								{!column.plain && <div className="size-3 shrink-0" />}
								{column.totalLabel !== "" && (
									<Text type="caption" color="primary" truncate className="min-w-0 font-semibold">
										{column.totalLabel}
									</Text>
								)}
								<Text type="caption" color="primary" className="ml-auto shrink-0 font-semibold">
									{display(Currency.sum(column.segments.map((segment) => segment.amount)))}
								</Text>
							</div>
						)}
					</div>
				))}
			</div>
		</div>
	);
};
